/*
  Store customization (Murmurs) - customer reviews on the product page.
  Paired with snippets/murmurs-product-reviews.liquid.

  Four jobs:
    1. Clamp long review text to N lines and show a Read more toggle when it overflows.
    2. Reveal further reviews in batches behind a 'Load more' button.
    3. Step a review's photo strip with the prev/next arrows (only rendered for 3+ photos).
    4. Open a photo in a lightbox, and make the easy save paths inconvenient.

  On (2): every review is already in the HTML - the extra ones just carry `hidden`.
  Nothing is fetched. That is deliberate: crawlers and the Product JSON-LD see the
  full set, so the marked-up aggregateRating always matches what the page carries.

  On (4): the lightbox paints the photo as a CSS background rather than an <img>,
  and right-click / drag are suppressed. That removes 'Save image as' and drag-to-
  desktop. It is friction, not protection - the file is still reachable through dev
  tools or the network tab, as it is on every website. Never describe it as secure.

  Clamping is visual only; the full text is always in the DOM.
*/
(function () {
  'use strict';

  var initialised = false;
  var lightbox = null;

  /* ---------------- read more ----------------

     Wiring and measuring are separate on purpose: a review revealed by 'Load more'
     needs re-measuring (a hidden element reports zero height, so overflow cannot be
     detected while it is hidden) but must NOT have its click handler attached twice.
  */

  function clampParts(textEl) {
    var body = textEl.closest('.murmurs-review__body');
    return {
      body: body,
      button: body ? body.querySelector('[data-murmurs-more]') : null
    };
  }

  function measureClamp(textEl) {
    var parts = clampParts(textEl);
    if (!parts.button) return;

    // A hidden element has no layout, so overflow cannot be measured yet.
    if (!textEl.getClientRects().length) return;

    textEl.classList.add('is-clamped');
    var overflows = textEl.scrollHeight - textEl.clientHeight > 1;

    if (!overflows) {
      textEl.classList.remove('is-clamped');
      parts.button.hidden = true;
      return;
    }

    parts.button.hidden = false;
    parts.button.setAttribute('aria-expanded', 'false');
    parts.button.textContent = parts.button.dataset.labelMore || 'Read more';
  }

  function wireClamp(textEl) {
    if (textEl.dataset.murmursClampWired === 'true') return;

    var parts = clampParts(textEl);
    if (!parts.button) return;

    parts.button.addEventListener('click', function () {
      var clamped = textEl.classList.toggle('is-clamped');
      parts.button.setAttribute('aria-expanded', clamped ? 'false' : 'true');
      parts.button.textContent = clamped
        ? parts.button.dataset.labelMore || 'Read more'
        : parts.button.dataset.labelLess || 'Read less';
    });

    textEl.dataset.murmursClampWired = 'true';
  }

  function setupClamp(textEl) {
    wireClamp(textEl);
    measureClamp(textEl);
  }

  /* ---------------- load more ---------------- */

  function refreshRevealed(review) {
    review.querySelectorAll('[data-murmurs-clamp]').forEach(measureClamp);
    review.querySelectorAll('[data-murmurs-photos]').forEach(function (wrapper) {
      if (typeof wrapper.murmursSyncArrows === 'function') wrapper.murmursSyncArrows();
    });
  }

  function setupLoadMore(list) {
    if (list.dataset.murmursLoadMoreReady === 'true') return;

    var scope = list.parentElement;
    var button = scope ? scope.querySelector('[data-murmurs-load-more]') : null;
    if (!button) return;

    var step = Number(button.dataset.step) || 3;

    button.addEventListener('click', function () {
      var hidden = list.querySelectorAll('.murmurs-review[hidden]');
      var count = Math.min(step, hidden.length);
      var first = null;

      for (var i = 0; i < count; i++) {
        hidden[i].hidden = false;
        refreshRevealed(hidden[i]);
        if (!first) first = hidden[i];
      }

      if (!list.querySelector('.murmurs-review[hidden]')) {
        button.hidden = true;
      }

      // Send keyboard and screen-reader users to the first review just revealed,
      // otherwise focus is left on a button that may have just disappeared.
      if (first) {
        first.setAttribute('tabindex', '-1');
        first.focus({ preventScroll: true });
      }
    });

    list.dataset.murmursLoadMoreReady = 'true';
  }

  /* ---------------- photo strip ---------------- */

  function blockEvent(event) {
    event.preventDefault();
    return false;
  }

  function setupPhotos(wrapper) {
    if (wrapper.dataset.murmursPhotosReady === 'true') return;

    var track = wrapper.querySelector('[data-murmurs-photos-track]');
    if (!track) return;

    // Make the easy save paths inconvenient on the thumbnails too.
    wrapper.addEventListener('contextmenu', blockEvent);
    wrapper.addEventListener('dragstart', blockEvent);

    wrapper.querySelectorAll('[data-murmurs-lightbox]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openLightbox(btn);
      });
    });

    var prev = wrapper.querySelector('[data-murmurs-photos-prev]');
    var next = wrapper.querySelector('[data-murmurs-photos-next]');

    if (prev && next) {
      var syncArrows = function () {
        var maxScroll = track.scrollWidth - track.clientWidth;
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= maxScroll - 1;
      };

      var step = function (direction) {
        var slide = track.querySelector('.murmurs-review__photo');
        var amount = slide ? slide.getBoundingClientRect().width + 8 : track.clientWidth;
        var max = track.scrollWidth - track.clientWidth;
        var from = track.scrollLeft;
        var target = Math.max(0, Math.min(max, from + amount * direction));

        try {
          track.scrollTo({ left: target, behavior: 'smooth' });
        } catch (e) {
          track.scrollLeft = target;
        }

        // Smooth scrolling is throttled or cancelled outright in some contexts
        // (a backgrounded tab, reduced-motion settings, older engines). If nothing
        // has moved shortly after, jump instead so the arrows always do something.
        window.setTimeout(function () {
          if (track.scrollLeft === from && from !== target) {
            track.scrollLeft = target;
          }
          syncArrows();
        }, 320);
      };

      prev.addEventListener('click', function () {
        step(-1);
      });
      next.addEventListener('click', function () {
        step(1);
      });
      track.addEventListener('scroll', syncArrows, { passive: true });

      // Exposed so a review revealed by 'Load more' can re-sync: a hidden track
      // reports zero width, which would otherwise leave both arrows disabled.
      wrapper.murmursSyncArrows = syncArrows;
      syncArrows();
    }

    wrapper.dataset.murmursPhotosReady = 'true';
  }

  /* ---------------- lightbox ---------------- */

  function buildLightbox() {
    if (lightbox) return lightbox;

    var template = document.querySelector('[data-murmurs-lightbox-template]');
    if (!template) return null;

    var root = template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(root);

    lightbox = {
      root: root,
      stage: root.querySelector('[data-murmurs-lightbox-stage]'),
      counter: root.querySelector('[data-murmurs-lightbox-counter]'),
      prev: root.querySelector('[data-murmurs-lightbox-prev]'),
      next: root.querySelector('[data-murmurs-lightbox-next]'),
      items: [],
      index: 0,
      opener: null
    };

    root.querySelectorAll('[data-murmurs-lightbox-close]').forEach(function (el) {
      el.addEventListener('click', closeLightbox);
    });
    lightbox.prev.addEventListener('click', function () {
      move(-1);
    });
    lightbox.next.addEventListener('click', function () {
      move(1);
    });
    root.addEventListener('contextmenu', blockEvent);
    root.addEventListener('dragstart', blockEvent);

    return lightbox;
  }

  function render() {
    var item = lightbox.items[lightbox.index];
    if (!item) return;

    lightbox.stage.style.backgroundImage = 'url("' + item.url + '")';
    lightbox.stage.setAttribute('aria-label', item.label || 'Customer photo');

    var many = lightbox.items.length > 1;
    lightbox.prev.hidden = !many;
    lightbox.next.hidden = !many;
    lightbox.counter.hidden = !many;
    if (many) {
      lightbox.counter.textContent = lightbox.index + 1 + ' / ' + lightbox.items.length;
    }
  }

  function move(direction) {
    var total = lightbox.items.length;
    lightbox.index = (lightbox.index + direction + total) % total;
    render();
  }

  function openLightbox(button) {
    var box = buildLightbox();
    if (!box) return;

    var group = button.closest('[data-murmurs-photos]');
    if (!group) return;

    box.items = Array.prototype.map.call(
      group.querySelectorAll('[data-murmurs-lightbox]'),
      function (b) {
        var img = b.querySelector('img');
        return {
          url: b.dataset.full,
          label: img ? img.getAttribute('alt') : 'Customer photo'
        };
      }
    );

    box.index = Number(button.dataset.index) || 0;
    box.opener = button;

    render();
    box.root.hidden = false;
    document.documentElement.classList.add('murmurs-lightbox-open');
    document.addEventListener('keydown', onKeydown);

    var close = box.root.querySelector('button[data-murmurs-lightbox-close]');
    if (close) close.focus();
  }

  function closeLightbox() {
    if (!lightbox || lightbox.root.hidden) return;

    lightbox.root.hidden = true;
    lightbox.stage.style.backgroundImage = '';
    document.documentElement.classList.remove('murmurs-lightbox-open');
    document.removeEventListener('keydown', onKeydown);

    if (lightbox.opener) {
      lightbox.opener.focus();
      lightbox.opener = null;
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      closeLightbox();
    } else if (event.key === 'ArrowLeft' && lightbox.items.length > 1) {
      move(-1);
    } else if (event.key === 'ArrowRight' && lightbox.items.length > 1) {
      move(1);
    }
  }

  /* ---------------- wiring ---------------- */

  function init(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-murmurs-list]').forEach(setupLoadMore);
    scope.querySelectorAll('[data-murmurs-clamp]').forEach(setupClamp);
    scope.querySelectorAll('[data-murmurs-photos]').forEach(setupPhotos);
  }

  function boot() {
    if (initialised) return;
    initialised = true;
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The accordion starts collapsed, so heights are only measurable once it opens.
  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target;
      if (!details || !details.classList || !details.classList.contains('murmurs-reviews__accordion')) return;
      if (!details.open) return;

      init(details);
      details.querySelectorAll('[data-murmurs-clamp]').forEach(measureClamp);
      details.querySelectorAll('[data-murmurs-photos]').forEach(function (wrapper) {
        if (typeof wrapper.murmursSyncArrows === 'function') wrapper.murmursSyncArrows();
      });
    },
    true
  );

  // Theme editor re-renders blocks without a page reload.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    init(event.target);
  });
})();
